import React, { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { registerPlugin } from "react-filepond";
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import { PcrAPI, PcrEdit, GetQrLink } from "../../../redux/requests/pcr";
import { preloaderToggle } from "../../../redux/slices/preloader";
import { linkStep, navigationStep } from "../../../redux/slices/navigation";
import StepCustomize from "../../../components/stepCustomize";
import { Context } from "../../../components/saveFile/saveFile";
import Navigation from "../../../components/navigation";
import SecondScreen from "../../SecondScreen";
import QrTitleField from "../../../components/input/qrTitleField";
import { getFileFromUrl, handleFilesUpdate } from "../../../hooks/helpers";
import { setIsStorageModal } from "../../../redux/slices/modals";
import { isNoEditRoute } from "../../../hooks/helpers";
import axios from "axios";
import { setCategory } from "../../../redux/slices/selectCategories";
import Form from "react-bootstrap/Form";
import FileUpload from "../../../components/input/fileUpload";
import InputText from "../../../components/input/inputText";
import usePremium from "../../../hooks/usePremium";
import useTypeDecription from "../../../hooks/useTypeDecription";
import DateInput from "../../../components/input/date";
import { useDebouncedCallback } from "use-debounce";
import useGetDataError from "../../../hooks/useGetDataError";
import useCurrentLocation from "../../../hooks/useCurrentLocation";
import useUserInfo from "../../../hooks/useUserInfo";

export default function PcrPage() {
  const {
    lang,
    getQrLinkSliceId,
    categoryChange,
    vcardDateLabel,
    toggle,
    getQrLinkSliceLink,
    status,
    locale,
  } = useSelector((state) => ({
    lang: state.changeLang.language.stepTwo.pcr,
    getQrLinkSliceId: state.getQrLinkSlice.id,
    categoryChange: state.categories.categoryChange,
    vcardDateLabel: state.changeLang.language.stepTwo.vcard,
    toggle: state.preloader.toggle,
    getQrLinkSliceLink: state.getQrLinkSlice.link,
    status: state.getQrLinkSlice.status,
    locale: state.changeLang.change,
  }));
  const { id } = useParams();
  const dispatch = useDispatch();
  let navigate = useNavigate();
  const { isPremium } = usePremium();
  const { content, getContent } = useTypeDecription();
  const { checkErrorStatus } = useGetDataError();
  const debouncedHandleFilesUpdate = useDebouncedCallback(
    handleFilesUpdate,
    500
  );
  const { adminEntryLocation } = useCurrentLocation();

  const { savePcr, setSavePcr } = useContext(Context);
  const [qrTitle, setQrTitle] = useState("");
  const [pcr, setPcr] = useState("");
  const [pcrFile, setPcrFile] = useState("");
  const [pcrDate, setPcrDate] = useState("");
  const [minDate, setPcrMinDate] = useState("");
  const [pcrPassword, setPcrPassword] = useState("");
  const [empty, setEmpty] = useState(false);
  const [errorFileMessage, setErrorFileMessage] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const { isAdmin } = useUserInfo();

  const maxFileSize = isAdmin ? null : isPremium ? 99 : 10;

  registerPlugin(
    FilePondPluginFileValidateSize,
    FilePondPluginFileValidateType
  );

  useEffect(() => {
    getContent("pcr");
  }, []);

  useEffect(() => {
    if (id) {
      axios
        .get(`/entry/file/get/${id}`)
        .then(({ data }) => {
          setQrTitle(data.qrTitle);
          setPcrPassword(data.password || "");
          setPcrDate(data.time?.split("T")[0] || "");
          dispatch(setCategory(data.adsCategories));

          if (data.link) {
            getFileFromUrl(data.link, "file").then((file) => {
              setPcrFile([file]);
            });
          }
        })
        .catch((err) => {
          checkErrorStatus(err.response.status);
        });
    }
  }, [id]);

  useEffect(() => {
    if (!!pcrFile && !!pcrFile[0]?.file) {
      setPcr([pcrFile[0]?.file, pcrDate, pcrPassword, qrTitle]);
      dispatch(linkStep(true));
    } else {
      dispatch(linkStep(false));
    }
  }, [pcrFile, pcrDate, pcrPassword, qrTitle]);

  useEffect(() => {
    let today = new Date();
    today.setDate(today.getDate() + 7);
    let dd = today.getDate();
    let mm = today.getMonth() + 1;
    let yyyy = today.getFullYear();
    if (dd < 10) {
      dd = "0" + dd;
    }
    if (mm < 10) {
      mm = "0" + mm;
    }

    today = yyyy + "-" + mm + "-" + dd;
    setPcrMinDate(today);
  }, []);

  const nextStep = () => {
    dispatch(
      GetQrLink(() => PcrEdit([getQrLinkSliceId || id, ...pcr, categoryChange]))
    );
    dispatch(preloaderToggle(true));
  };

  useEffect(() => {
    if (status === "success" && !!getQrLinkSliceLink && toggle) {
      if (isNoEditRoute) {
        dispatch(navigationStep(3));
        setSavePcr([pcrFile, pcrDate, pcrPassword, qrTitle]);
        navigate("/qr");
      } else {
        window.location.href = adminEntryLocation;
      }
    } else if (status === "failed") {
      dispatch(preloaderToggle(false));
    }
  }, [status]);

  function step() {
    if (
      !!pcr &&
      (isAdmin || pcr[0]?.size <= maxFileSize * 1024 * 1024) &&
      getQrLinkSliceId === null &&
      !id
    ) {
      setEmpty(false);
      dispatch(GetQrLink(() => PcrAPI([...pcr, categoryChange])));
      dispatch(preloaderToggle(true));
    } else {
      setEmpty(true);
    }

    if (!!getQrLinkSliceId || id) {
      if (!!savePcr && savePcr[0]?.file === pcr[0]?.file && !id) {
        navigate("/qr");
        setTimeout(() => {
          dispatch(preloaderToggle(false));
        }, 0);
      }
      if ((savePcr && savePcr[0]?.file !== pcr[0]?.file) || id) {
        nextStep();
        return;
      }
      if ((savePcr && savePcr[1] !== pcrDate) || id) {
        nextStep();
        return;
      }
      if ((savePcr && savePcr[2] !== pcrPassword) || id) {
        nextStep();
        return;
      }
    }
  }

  useEffect(() => {
    if (!!savePcr && savePcr[0]) {
      setPcrFile(savePcr[0]),
        setPcrDate(savePcr[1]),
        setPcrPassword(savePcr[2]);
      setQrTitle(savePcr[3]);
    }
  }, [savePcr]);

  return (
    <>
      <Navigation />
      <SecondScreen>
        <Form className="col-12 col-lg-6 mx-auto">
          <FileUpload
            files={pcrFile}
            onupdatefiles={(files) => {
              setErrorFileMessage("");
              debouncedHandleFilesUpdate(
                files,
                setPcrFile,
                () => dispatch(setIsStorageModal(true)),
                !isAdmin
              );
            }}
            maxFileSize={maxFileSize ? `${maxFileSize}MB` : null}
            allowMultiple={false}
            maxFiles={1}
            name="pcrFile"
            allowFileTypeValidation={true}
            acceptedFileTypes={["application/pdf"]}
            label={lang[0]}
            popoverText={lang[19]}
            onerror={(e) => setErrorFileMessage(e)}
          />
          {errorFileMessage && (
            <Form.Text className="text-danger">{errorFileMessage}</Form.Text>
          )}
          {empty &&
            (!pcrFile.length ||
              (!isAdmin && pcrFile[0]?.size > maxFileSize * 1024 * 1024)) && (
              <Form.Text className="text-danger">{lang[4]}</Form.Text>
            )}

          <DateInput
            placeholder={vcardDateLabel[24]}
            autoComplete="off"
            name="pcrData"
            min={minDate}
            value={pcrDate}
            onChange={(e) => setPcrDate(e)}
            locale={locale}
            label={lang[1]}
            popoverText={lang[20]}
          />

          <InputText
            type={isPasswordVisible ? "text" : "password"}
            placeholder={lang[2]}
            name="pcrPassword"
            autoComplete="new-password"
            value={pcrPassword}
            onChange={(e) => setPcrPassword(e.target.value)}
            onFocus={() => setIsPasswordVisible(true)}
            onBlur={() => setIsPasswordVisible(false)}
            popoverText={lang[21]}
          />

          <QrTitleField
            value={qrTitle}
            onChange={(e) => setQrTitle(e.target.value)}
          />
        </Form>

        <StepCustomize
          emulate={step}
          disabled={
            !pcrFile.length ||
            (!isAdmin && pcrFile[0]?.size > maxFileSize * 1024 * 1024) ||
            errorFileMessage
          }
        />
      </SecondScreen>

      {isNoEditRoute && content}
    </>
  );
}
